/* Comments on a project or one of its tasks, for both apps (backend services/comments.py):
   threads one reply deep, unread ones on top, @mentions of the team and the client's people,
   edit and delete of your own (administrators: anyone's), and, for staff, a thread's
   Client-visible flag. Task threads on the project's board link to their task (onOpenTask), and
   a question's thread to the question (onOpenQuestion), whose status decides who sees it.

   Mentions are stored as @[Name](user id). While typing they read as plain @Name: picking
   someone from the list remembers who, and the text is turned back into tokens when saved. */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Api } from "../api/client";
import { formatRelative } from "../format";
import { Badge, Button, Check, EmptyState, ErrorNote, Loading, cx } from "./basics";
import { useLiveReload } from "./live";

export interface CommentPerson {
  id: string;
  name: string;
  is_client: boolean;
}

export interface CommentItem {
  id: string;
  thread_id: string;
  author: CommentPerson;
  body: string;
  mentions: { id: string; name: string }[];
  created_at: string;
  edited_at: string | null;
  unread: boolean;
  can_edit: boolean;
  /** The answer to the thread's question. */
  is_answer?: boolean;
  /** In a question's discussion: the comment this one sits under. */
  reply_to_id?: string | null;
}

export interface CommentThread {
  id: string;
  task: { id: string; key: string; title: string; shared: boolean } | null;
  /** The feature request the thread is on (UX-196). */
  feature_request?: { id: string; key: string; title: string; shared: boolean } | null;
  /** The question the thread is about: its comments are the question's. */
  question?: { id: string; title: string } | null;
  client_visible: boolean;
  root: CommentItem;
  replies: CommentItem[];
  unread: number;
}

export interface CommentBoard {
  threads: CommentThread[];
  unread_count: number;
}

/** A top-level comment of a question's discussion and the replies under it. */
export interface CommentGroup {
  comment: CommentItem;
  replies: CommentItem[];
}

/** A discussion as people read it, keeping the comments' order: a comment with no reply_to_id is
    top-level, and so is one whose comment isn't in the list, rather than vanishing. */
export function groupReplies(comments: CommentItem[]): CommentGroup[] {
  const ids = new Set(comments.map((c) => c.id));
  const tops = new Map(
    comments
      .filter((c) => !c.reply_to_id || !ids.has(c.reply_to_id))
      .map((c): [string, CommentGroup] => [c.id, { comment: c, replies: [] }]),
  );
  const groups: CommentGroup[] = [];
  for (const c of comments) {
    const own = tops.get(c.id);
    const under = c.reply_to_id ? tops.get(c.reply_to_id) : undefined;
    if (own) groups.push(own);
    else if (under) under.replies.push(c);
    else groups.push({ comment: c, replies: [] });
  }
  return groups;
}

/** The delete confirmation of a comment with replies under it: they go with it. */
export function deleteWithReplies(count: number): string | undefined {
  if (count === 0) return undefined;
  return `Delete this comment and its ${count === 1 ? "reply" : `${count} replies`}?`;
}

const MENTION = /@\[([^\]\n]{1,200})\]\(([0-9a-fA-F-]{36})\)/g;

/** A stored body as people type it: @Name for each mention, and who each name is. */
export function toDraft(body: string): { text: string; picked: Record<string, string> } {
  const picked: Record<string, string> = {};
  const text = body.replace(MENTION, (_m, name: string, id: string) => {
    picked[name] = id;
    return `@${name}`;
  });
  return { text, picked };
}

/** A draft as stored: each @Name of someone picked becomes @[Name](id). Longer names first, so
    "@Jo Smith" isn't taken for "@Jo". */
export function fromDraft(text: string, picked: Record<string, string>): string {
  const names = Object.keys(picked).sort((a, b) => b.length - a.length);
  if (!names.length) return text.trim();
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`@(${escaped.join("|")})(?![\\w])`, "g");
  return text.replace(re, (_m, name: string) => `@[${name}](${picked[name]})`).trim();
}

/** The @word being typed at the caret, if any: where it starts and what's typed so far. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const m = /(^|\s)@([^\s@]{0,30})$/.exec(before);
  if (!m) return null;
  const query = m[2] ?? "";
  return { start: caret - query.length - 1, query };
}

/** A body with its mentions highlighted. */
export function CommentBody({ body }: { body: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const m of body.matchAll(MENTION)) {
    const at = m.index ?? 0;
    if (at > last) parts.push(body.slice(last, at));
    parts.push(
      <span className="mention" key={at}>
        @{m[1]}
      </span>,
    );
    last = at + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return <div className="cm-body">{parts}</div>;
}

export function Composer({
  people,
  initial = "",
  placeholder,
  submitLabel,
  busy,
  error,
  onSubmit,
  onCancel,
  extra,
  autoFocus,
  onDraftChange,
}: {
  people: CommentPerson[];
  initial?: string;
  placeholder: string;
  submitLabel: string;
  busy: boolean;
  error: unknown;
  onSubmit: (body: string) => Promise<boolean>;
  onCancel?: () => void;
  extra?: ReactNode;
  autoFocus?: boolean;
  /** Whether something is typed and not yet posted. */
  onDraftChange?: (drafting: boolean) => void;
}) {
  const start = useMemo(() => toDraft(initial), [initial]);
  const [text, setText] = useState(start.text);
  const drafting = text.trim() !== "";
  const draftRef = useRef(onDraftChange);
  draftRef.current = onDraftChange;
  useEffect(() => draftRef.current?.(drafting), [drafting]);
  useEffect(() => () => draftRef.current?.(false), []);
  const [picked, setPicked] = useState(start.picked);
  const [query, setQuery] = useState<{ start: number; query: string } | null>(null);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);
  // Where the caret goes once a picked name is in the box. Set in the same commit as the text, not
  // on a later frame, which could land after more typing and move the caret back into it.
  const caretAfterPick = useRef<number | null>(null);
  useLayoutEffect(() => {
    const at = caretAfterPick.current;
    if (at === null) return;
    caretAfterPick.current = null;
    ref.current?.focus();
    ref.current?.setSelectionRange(at, at);
  });
  const matches = query
    ? people.filter((p) => p.name.toLowerCase().includes(query.query.toLowerCase())).slice(0, 6)
    : [];

  const pick = (p: CommentPerson) => {
    if (!query) return;
    const caret = ref.current?.selectionStart ?? text.length;
    const next = `${text.slice(0, query.start)}@${p.name} ${text.slice(caret)}`;
    setText(next);
    setPicked((m) => ({ ...m, [p.name]: p.id }));
    setQuery(null);
    caretAfterPick.current = query.start + p.name.length + 2;
  };

  const submit = async () => {
    if (!text.trim()) return;
    if (await onSubmit(fromDraft(text, picked))) {
      setText("");
      setPicked({});
    }
  };

  return (
    <div className="cm-compose">
      {!!error && <ErrorNote error={error} />}
      <div className="cm-input">
        <textarea
          ref={ref}
          value={text}
          rows={3}
          maxLength={10000}
          placeholder={placeholder}
          aria-label={placeholder}
          autoFocus={autoFocus}
          onChange={(e) => {
            setText(e.target.value);
            setQuery(mentionQuery(e.target.value, e.target.selectionStart));
            setActive(0);
          }}
          onKeyDown={(e) => {
            if (matches.length) {
              if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const step = e.key === "ArrowDown" ? 1 : -1;
                setActive((i) => (i + step + matches.length) % matches.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                const chosen = matches[active];
                if (chosen) pick(chosen);
                return;
              }
              if (e.key === "Escape") {
                e.stopPropagation();
                setQuery(null);
                return;
              }
            }
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        {matches.length > 0 && (
          <ul className="cm-mentions" role="listbox" aria-label="Mention someone">
            {matches.map((p, i) => (
              <li key={p.id} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  className={cx(i === active && "on")}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(p);
                  }}
                >
                  {p.name}
                  {p.is_client && <span className="muted"> · client</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="cm-actions">
        {extra}
        <span style={{ flex: 1 }} />
        {onCancel && (
          <Button size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button size="sm" variant="primary" busy={busy} disabled={!text.trim()} onClick={() => void submit()}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

export interface Actions {
  people: CommentPerson[];
  canSetVisibility: boolean;
  /** False on a task's own comments when the client can't see the task. */
  taskShared: boolean;
  inTask: boolean;
  onOpenTask?: (taskId: string) => void;
  onOpenQuestion?: (questionId: string) => void;
  run: (fn: () => Promise<unknown>) => Promise<boolean>;
  busy: boolean;
  error: unknown;
  api: Api;
  base: string;
}

export function Comment({
  comment,
  a,
  root,
  tools,
  deleteText,
}: {
  comment: CommentItem;
  a: Actions;
  root?: CommentThread;
  /** More buttons beside Edit and Delete (a question's Mark as answer). */
  tools?: ReactNode;
  /** The delete confirmation, when more than this comment goes with it. */
  deleteText?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  return (
    <div className={cx("cm", comment.unread && "unread")} id={`comment-${comment.id}`}>
      <div className="cm-head">
        <strong>{comment.author.name}</strong>
        {comment.author.is_client && <Badge kind="info">Client</Badge>}
        <span className="muted" title={new Date(comment.created_at).toLocaleString()}>
          {formatRelative(comment.created_at)}
          {comment.edited_at && " · edited"}
        </span>
        {comment.unread && <Badge kind="acc">New</Badge>}
        {comment.is_answer && <Badge kind="ok">Answer</Badge>}
        {!editing && !confirming && (tools || comment.can_edit) && (
          // One group, pushed right: on a narrow screen it drops below the name as a whole, rather
          // than leaving Delete alone on a line.
          <span className="cm-tools">
            {tools}
            {comment.can_edit && (
              <>
                <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
                  Delete
                </Button>
              </>
            )}
          </span>
        )}
      </div>
      {editing ? (
        <Composer
          people={a.people}
          initial={comment.body}
          placeholder="Edit your comment"
          submitLabel="Save"
          busy={a.busy}
          error={a.error}
          autoFocus
          onCancel={() => setEditing(false)}
          onSubmit={async (body) => {
            const ok = await a.run(() => a.api.patch(`${a.base}/${comment.id}`, { body }));
            if (ok) setEditing(false);
            return ok;
          }}
        />
      ) : (
        <CommentBody body={comment.body} />
      )}
      {confirming && (
        <div className="cm-confirm" role="alert">
          <span>
            {deleteText ??
              (root?.replies.length ? "Delete this comment and its replies?" : "Delete this comment?")}
          </span>
          <span className="cm-tools">
            <Button size="sm" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
            <Button
              size="sm"
              variant="danger"
              busy={a.busy}
              onClick={() => void a.run(() => a.api.del(`${a.base}/${comment.id}`))}
            >
              Delete
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}

function ThreadView({ thread, a, highlight }: { thread: CommentThread; a: Actions; highlight: boolean }) {
  const [replying, setReplying] = useState(false);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView?.({ block: "center" });
  }, [highlight]);
  // A task's threads can only be shown to the client once the task is shared with them.
  const showable = thread.task
    ? thread.task.shared
    : thread.feature_request
      ? thread.feature_request.shared
      : a.taskShared;
  // A question's thread is shown to the client while they see the question, not by hand.
  const visibility =
    a.canSetVisibility && !thread.question && thread.root.can_edit && (thread.client_visible || showable);
  // On a task's own comments, threads don't repeat which task they're on.
  const task = a.inTask ? null : (thread.task ?? thread.feature_request ?? null);
  // Only staff are told a thread is client-visible: everything the client sees is.
  const visibleTag = a.canSetVisibility && thread.client_visible;
  // Only a question's discussion nests: every other thread's replies are all top-level here.
  const [top, ...later] = groupReplies([thread.root, ...thread.replies]);
  return (
    <article
      ref={ref}
      className={cx("cm-thread", highlight && "hl")}
      aria-label={`Thread by ${thread.root.author.name}`}
    >
      {(task || thread.question || visibleTag || thread.unread > 0) && (
        <div className="cm-tags">
          {thread.question &&
            (a.onOpenQuestion ? (
              <button
                type="button"
                className="lk cm-task"
                onClick={() => a.onOpenQuestion?.(thread.question!.id)}
              >
                Question: {thread.question.title}
              </button>
            ) : (
              <span className="cm-task">Question: {thread.question.title}</span>
            ))}
          {task &&
            (a.onOpenTask && thread.task ? (
              <button type="button" className="lk cm-task" onClick={() => a.onOpenTask?.(task.id)}>
                {task.key} · {task.title}
              </button>
            ) : (
              <span className="cm-task">
                {task.key} · {task.title}
              </span>
            ))}
          {visibleTag && <Badge kind="info">Client-visible</Badge>}
          <span style={{ flex: 1 }} />
          {thread.unread > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void a.run(() =>
                  a.api.post(`${a.base}/read`, {
                    comment_ids: [thread.root, ...thread.replies].filter((c) => c.unread).map((c) => c.id),
                  }),
                )
              }
            >
              Mark read
            </Button>
          )}
        </div>
      )}
      <Comment comment={thread.root} a={a} root={thread} />
      {thread.replies.length > 0 && (
        <div className="cm-replies">
          {top?.replies.map((r) => (
            <Comment key={r.id} comment={r} a={a} />
          ))}
          {later.map((g) => (
            <div className="cm-group" key={g.comment.id}>
              <Comment comment={g.comment} a={a} deleteText={deleteWithReplies(g.replies.length)} />
              {g.replies.length > 0 && (
                <div className="cm-replies">
                  {g.replies.map((r) => (
                    <Comment key={r.id} comment={r} a={a} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="cm-foot">
        {replying ? (
          <Composer
            people={a.people.filter((p) => thread.client_visible || !p.is_client)}
            placeholder="Write a reply… (@ to mention)"
            submitLabel="Reply"
            busy={a.busy}
            error={a.error}
            autoFocus
            onCancel={() => setReplying(false)}
            onSubmit={async (body) => {
              const ok = await a.run(() => a.api.post(a.base, { body, parent_id: thread.id }));
              if (ok) setReplying(false);
              return ok;
            }}
          />
        ) : (
          <>
            <Button size="sm" onClick={() => setReplying(true)}>
              Reply
            </Button>
            {visibility && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  void a.run(() =>
                    a.api.patch(`${a.base}/${thread.id}`, { client_visible: !thread.client_visible }),
                  )
                }
              >
                {thread.client_visible ? "Hide from client" : "Show to client"}
              </Button>
            )}
          </>
        )}
      </div>
    </article>
  );
}

export function Comments({
  api,
  base,
  taskId,
  featureRequestId,
  canSetVisibility = false,
  taskShared = true,
  onOpenTask,
  onOpenQuestion,
  onUnread,
  highlight,
}: {
  api: Api;
  /** The project's comments path in this app's API: `/projects/<id>/comments`. */
  base: string;
  /** Only this task's threads; new ones go on it. */
  taskId?: string;
  /** Only this feature request's threads; new ones go on it (UX-196). */
  featureRequestId?: string;
  /** Staff: the Client-visible box and Show to / Hide from client. */
  canSetVisibility?: boolean;
  /** For a task's comments: whether the client can see the task (and so its threads). */
  taskShared?: boolean;
  onOpenTask?: (taskId: string) => void;
  /** A question's thread links to the question. */
  onOpenQuestion?: (questionId: string) => void;
  /** Told the unread count each time the board loads (the tab's counter). */
  onUnread?: (count: number) => void;
  /** A thread to scroll to and highlight (an email's link). */
  highlight?: string | null;
}) {
  const [board, setBoard] = useState<CommentBoard | null>(null);
  const [people, setPeople] = useState<CommentPerson[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(false);
  const unreadRef = useRef(onUnread);
  unreadRef.current = onUnread;

  // Whose threads these are: a task's, a feature request's (UX-196), or the whole project's.
  const scope = useMemo((): Record<string, string> | undefined => {
    if (taskId) return { task_id: taskId };
    if (featureRequestId) return { feature_request_id: featureRequestId };
    return undefined;
  }, [taskId, featureRequestId]);
  const load = useCallback(async () => {
    try {
      const b = await api.get<CommentBoard>(base, { query: scope });
      setBoard(b);
      setLoadError(null);
      unreadRef.current?.(b.unread_count);
    } catch (e) {
      setLoadError(e);
    }
  }, [api, base, scope]);

  useEffect(() => {
    void load();
    api
      .get<CommentPerson[]>(`${base}/people`)
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [api, base, load]);
  // Someone else commented (UX-187): reload around whatever is being typed.
  useLiveReload(load);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (loadError && !board) return <ErrorNote error={loadError} />;
  if (!board) return <Loading />;

  const a: Actions = {
    people,
    canSetVisibility,
    taskShared,
    inTask: !!scope,
    onOpenTask,
    onOpenQuestion,
    run,
    busy,
    error,
    api,
    base,
  };
  const unread = board.threads.filter((t) => t.unread > 0);
  const rest = board.threads.filter((t) => t.unread === 0);
  const composerPeople = canSetVisibility ? people.filter((p) => visible || !p.is_client) : people;

  return (
    <div className="cm-board" data-live-safe="">
      <Composer
        people={composerPeople}
        placeholder={
          taskId
            ? "Comment on this task… (@ to mention)"
            : featureRequestId
              ? "Comment on this feature request… (@ to mention)"
              : "Write a comment… (@ to mention)"
        }
        submitLabel="Comment"
        busy={busy}
        error={error}
        extra={
          canSetVisibility && (
            <span title={taskShared ? undefined : "Share the task with the client first"}>
              <Check checked={visible && taskShared} disabled={!taskShared} onChange={setVisible}>
                Client-visible
              </Check>
            </span>
          )
        }
        onSubmit={(body) =>
          run(() =>
            api.post(base, {
              body,
              ...scope,
              ...(canSetVisibility ? { client_visible: visible && taskShared } : {}),
            }),
          )
        }
      />
      {unread.length > 0 && (
        <section className="cm-section" aria-label="Unread">
          <div className="cm-section-h">
            <h3>
              Unread <span className="cnt">{board.unread_count}</span>
            </h3>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void run(() => api.post(`${base}/read`, scope ?? {}))}
            >
              Mark all read
            </Button>
          </div>
          {unread.map((t) => (
            <ThreadView key={t.id} thread={t} a={a} highlight={t.id === highlight} />
          ))}
        </section>
      )}
      {rest.length > 0 && (
        <section className="cm-section" aria-label="All comments">
          {unread.length > 0 && (
            <div className="cm-section-h">
              <h3>Earlier</h3>
            </div>
          )}
          {rest.map((t) => (
            <ThreadView key={t.id} thread={t} a={a} highlight={t.id === highlight} />
          ))}
        </section>
      )}
      {board.threads.length === 0 && <EmptyState>No comments yet.</EmptyState>}
    </div>
  );
}
